import { postLocationShareRequestUrl } from '../fetchPublicServices';

export type SmartPhraseContext = {
  callerID?: string;
  token?: string;
  orgName?: string;
  psapName?: string;
};

export type SmartPhrase = {
  key: string; // key to identify the smart phrase
  resolve: (ctx: SmartPhraseContext) => Promise<string> | string; // function to resolve the smart phrase, can be async
  example?: string; // example resolve value
  display: string; // value that can be displayed in the UI when the smart phrase is not resolved yet
  description: string; // description of the smart phrase for documentation purposes - useful for tooltips
};

export type SmartPhraseKey = (typeof smartPhrases)[number]['key'];

export type TextSegment = { type: 'text'; content: string };
export type PhraseSegment = { type: 'phrase'; phrase?: SmartPhrase };
export type ParsedSegment = TextSegment | PhraseSegment;

export const isPhraseSegment = (
  segment: ParsedSegment,
): segment is PhraseSegment => {
  return segment.type === 'phrase';
};

export const isTextSegment = (
  segment: ParsedSegment,
): segment is TextSegment => {
  return segment.type === 'text';
};

export const smartPhrases = [
  {
    key: 'LocationRequestLink',
    resolve: async ({ callerID, token, orgName }) => {
      if (!callerID || !token || !orgName) {
        throw new Error('Missing required context for LocationRequestLink');
      }

      return postLocationShareRequestUrl(callerID, orgName, token);
    },
    display: 'Location Request Link',
    description: "A link to request the recipient's location.",
  },
  {
    key: 'AgencyName',
    resolve: ({ psapName }) => {
      if (!psapName) {
        throw new Error('Missing required context for AgencyName');
      }

      return psapName;
    },
    display: 'Agency Name',
    description: 'The name of your agency.',
  },
] as const satisfies SmartPhrase[];

export const interpolateSmartPhrases = async (
  text: string,
  ctx: SmartPhraseContext,
): Promise<string> => {
  let interpolatedText = text;
  for (const smartPhrase of smartPhrases) {
    const key = `{{${smartPhrase.key}}}`;

    if (interpolatedText.includes(key)) {
      try {
        const resolvedValue = await smartPhrase.resolve(ctx);

        interpolatedText = interpolatedText.replaceAll(key, resolvedValue);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          `Error resolving smart phrase ${smartPhrase.key}:`,
          error,
        );
        // Leave placeholder unchanged on resolution error
      }
    }
  }
  return interpolatedText;
};

export const parseSmartPhrases = (text: string): ParsedSegment[] => {
  const segments: ParsedSegment[] = [];
  const phraseRegex = /\{\{(\w+)\}\}/g;
  let lastIndex = 0;
  let match;

  // Find all {{Key}} patterns and split text around them
  while ((match = phraseRegex.exec(text)) !== null) {
    // Add text before the phrase
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }

    const key = match[1];

    // Add the phrase segment
    segments.push({
      type: 'phrase',
      phrase: smartPhrases.find(p => p.key === key),
    });

    lastIndex = phraseRegex.lastIndex;
  }

  // Add remaining text after last phrase
  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }

  return segments;
};
